import React, { useMemo } from 'react';
import classnames from '../../util/classnames';


type LoaderProps = {
  children?: React.ReactNode,
  /** Legacy. Kept for backward compat with existing callers — maps to size. */
  sm?: boolean,
  text?: string | React.ReactNode,
  /** "primary" → accent colour, anything else → muted zinc. Default "primary". */
  color?: 'primary' | 'secondary',
  /** Explicit pixel size. When omitted, all callers get a CONSISTENT default. */
  size?: number | null,
  padding?: number,
  /** Center in the viewport with a backdrop-blurred overlay (used by app-boot
   *  and route-level loading). Renders at 60px to dominate the viewport. */
  fullscreen?: boolean,
  /** Three bouncing dots — for inline contexts (button labels, etc.). */
  dots?: boolean,
  /** Legacy. Treated the same as default. Kept for backward compat. */
  spinner?: boolean,
  inline?: boolean,
  className?: string,
};

/**
 * Single, modern loader used everywhere across the app — replicates the
 * UweStudioFrontend spinner so both apps look identical. The icon is the
 * `Loader2` shape from lucide-react inlined as SVG (a 3/4 circle arc with
 * rounded caps that spins via Tailwind's `animate-spin`). lucide-react is
 * NOT installed in this app, so we keep the asset embedded here to avoid
 * pulling a new dependency just for one icon.
 *
 * Size priority:
 *   1. `size` (explicit pixel override)
 *   2. `fullscreen` → 60px  (the boot/route spinner)
 *   3. `sm` / `spinner` → 20px  (inline contexts)
 *   4. default → 40px  (in-card loading)
 *
 * Colour: by default the spinner uses the tenant's `--accent-color`, so when
 * the theme changes the brand colour every loader in the app re-skins
 * automatically. Pass `color="secondary"` for a muted zinc spinner when the
 * accent would compete with the surrounding UI.
 */
function Loader({
  children,
  sm,
  text,
  color = 'primary',
  fullscreen,
  dots = false,
  spinner = false,
  inline = false,
  size = null,
  className = '',
}: LoaderProps) {
  const loaderSize = useMemo(() => {
    if (size) return size;
    if (fullscreen) return 60;
    if (sm || spinner) return 20;
    return 40;
  }, [size, sm, spinner, fullscreen]);

  // Tailwind utility for stroke colour. `theme-accent` reads --accent-color,
  // so changing the brand in the editor instantly recolours every loader.
  const colorClass = color === 'primary'
    ? 'theme-accent-text'
    : 'text-zinc-500 dark:text-zinc-400';

  const loaderImage = useMemo(() => {
    if (dots) {
      return (
        <span className="inline-flex items-center gap-1.5" aria-label="Loading" role="status">
          <span className="w-1.5 h-1.5 rounded-full theme-accent-bg animate-bounce [animation-delay:-0.3s]" />
          <span className="w-1.5 h-1.5 rounded-full theme-accent-bg animate-bounce [animation-delay:-0.15s]" />
          <span className="w-1.5 h-1.5 rounded-full theme-accent-bg animate-bounce" />
        </span>
      );
    }

    // Loader2 from lucide-react inlined as SVG. Single path — a 3/4 circle
    // with rounded line caps. `currentColor` propagates from the wrapping
    // class so theme changes apply without rebuilding the icon.
    return (
      <svg
        role="status"
        aria-label="Loading"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width={loaderSize}
        height={loaderSize}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`animate-spin ${colorClass}`}
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    );
  }, [dots, loaderSize, colorClass]);

  const loader = (
    <div
      className={classnames(
        'flex',
        inline ? 'flex-row items-center gap-3' : 'flex-col items-center justify-center gap-4',
        className
      )}
    >
      {loaderImage}
      {children && (
        <div className="theme-text-secondary text-sm font-medium">{children}</div>
      )}
    </div>
  );

  if (fullscreen) {
    // Same backdrop pattern as UweStudio: blurred semi-transparent overlay
    // covering the viewport, content centred. Reads cleanly on both light
    // and dark themes via `theme-bg-primary/80`.
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center theme-bg-primary/80 backdrop-blur-sm">
        {loader}
        {text && <p className="theme-text-muted mt-4 text-sm font-medium">{text}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-[100px] p-4">
      {loader}
      {text && <p className="theme-text-muted mt-3 text-sm">{text}</p>}
    </div>
  );
}


export default Loader;
